/** @format */

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	// Service imports
	fetchPayoutOverviewAsAdmin,
	fetchRetryablePayoutsAsAdmin,
	retryPayoutsForDateAsAdmin,
	retrySpecificPayoutAsAdmin,
	retryAllPayoutsAsAdmin,
	fetchPayoutsByCircleAsAdmin,
	fetchPayoutsByMemberAsAdmin,
	fetchPayoutsByCycleAsAdmin,
	fetchCirclePayoutDetailsAsAdmin,
	// Type imports
	RetryPayoutByDateParams,
	RetrySpecificPayoutParams,
	CirclePayoutDetailsParams,
} from "../services/adminPayOutService";

// ==================== Response Types ====================

export interface PayoutRecord {
	payoutNumber: number;
	date: string;
	dateFormatted: string;
	memberId: string;
	memberName: string;
	memberEmail?: string;
	circleName: string;
	circleId: string;
	cycleId: string;
	cycleStatus: string;
	status: "COMPLETED" | "MISSED" | "FAILED" | "DUE_TODAY" | "UPCOMING";
	amount?: number;
	processedAt?: string;
	rotationIndex: number;
	totalMembers: number;
	failureReason?: string;
	stripeTransferId?: string;
}

export interface PayoutSummary {
	totalCycles: number;
	totalScheduledPayouts: number;
	completed: number;
	missed: number;
	failed: number;
	dueToday: number;
	upcoming: number;
	totalPaidOutCents: number;
	completionRate: string;
}

export interface MemberPayoutStats {
	memberId: string;
	memberName: string;
	memberEmail?: string;
	completed: number;
	pending: number;
}

export interface CirclePayoutSummary {
	circleId: string;
	circleName: string;
	totalPayouts: number;
	completed: number;
	missed: number;
	failed: number;
	dueToday: number;
	upcoming: number;
	totalPaidCents: number;
	members: MemberPayoutStats[];
}

export interface PayoutOverviewResponse {
	summary: PayoutSummary;
	past: {
		completed: PayoutRecord[];
		missed: PayoutRecord[];
		failed: PayoutRecord[];
	};
	today: PayoutRecord[];
	upcoming: PayoutRecord[];
	byCircle: CirclePayoutSummary[];
}

export interface RetryablePayoutRecord {
	cycleId: string;
	unionId: string;
	circleName: string;
	payoutDate: string;
	payoutDateFormatted: string;
	payoutIndex: number;
	memberId: string;
	memberName: string;
	memberEmail: string;
	memberStripeAccountId: string | null;
	status: "MISSED" | "FAILED";
	failureReason?: string;
	daysOverdue: number;
}

export interface RetryablePayoutsResponse {
	total: number;
	missed: {
		count: number;
		payouts: RetryablePayoutRecord[];
	};
	failed: {
		count: number;
		payouts: RetryablePayoutRecord[];
	};
	all: RetryablePayoutRecord[];
}

export interface RetryPayoutResult {
	success: boolean;
	date: string;
	cyclesChecked: number;
	payoutsProcessed: number;
	payoutsFailed: number;
	message: string;
	details?: {
		memberName?: string;
		memberEmail?: string;
		circleName?: string;
		grossAmount?: number;
		netAmount?: number;
		feeAmount?: number;
		stripeTransferId?: string;
		payoutDate?: string;
	};
}

export interface BulkRetryResult {
	totalDates: number;
	totalPayoutsAttempted: number;
	totalPayoutsProcessed: number;
	totalPayoutsFailed: number;
	results: RetryPayoutResult[];
}

export interface PayoutsByFilterResponse {
	summary: {
		total: number;
		completed: number;
		missed: number;
		failed: number;
		dueToday: number;
		upcoming: number;
	};
	payouts: {
		completed: PayoutRecord[];
		missed: PayoutRecord[];
		failed: PayoutRecord[];
		dueToday: PayoutRecord[];
		upcoming: PayoutRecord[];
		all: PayoutRecord[];
	};
}

// ==================== Circle Payout Details Response Types ====================

export interface CirclePayoutMemberSummary {
	memberId: string;
	memberName: string;
	memberEmail?: string;
	payouts: {
		completed: number;
		missed: number;
		failed: number;
		upcoming: number;
		total: number;
		nextPayoutDate?: string;
	};
}

export interface CirclePayoutMonthGroup {
	month: string; // e.g., "January 2026"
	year: number;
	monthNumber: number;
	payouts: PayoutRecord[];
	summary: {
		total: number;
		completed: number;
		missed: number;
		failed: number;
		upcoming: number;
	};
}

export interface CirclePayoutNextPayout {
	date: string;
	dateFormatted: string;
	memberId: string;
	memberName: string;
	memberEmail?: string;
	payoutNumber: number;
	daysUntil: number;
}

export interface CirclePayoutDetailsResponse {
	circleId: string;
	circleName: string;
	cycleSummary: {
		totalCycles: number;
		activeCycleId?: string;
	};
	filters: {
		selectedDate?: string;
		dateRangeStart?: string;
		dateRangeEnd?: string;
	};
	summary: {
		total: number;
		completed: number;
		missed: number;
		failed: number;
		dueToday: number;
		upcoming: number;
		totalPaidCents: number;
	};
	nextPayout?: CirclePayoutNextPayout;
	payoutsByMonth: CirclePayoutMonthGroup[];
	allPayouts: PayoutRecord[];
	members: CirclePayoutMemberSummary[];
}

// ==================== Payout Overview Actions ====================

/**
 * Get complete payout overview (past, present, future payouts with statistics)
 */
export const getPayoutOverviewAsAdmin = createAsyncThunk(
	"adminPayout/getPayoutOverview",
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchPayoutOverviewAsAdmin();
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch payout overview",
			);
		}
	},
);

/**
 * Get all retryable payouts (missed and failed)
 */
export const getRetryablePayoutsAsAdmin = createAsyncThunk(
	"adminPayout/getRetryablePayouts",
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchRetryablePayoutsAsAdmin();
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch retryable payouts",
			);
		}
	},
);

// ==================== Payout Retry Actions ====================

/**
 * Retry all payouts for a specific date (YYYY-MM-DD)
 */
export const retryPayoutsForDate = createAsyncThunk(
	"adminPayout/retryPayoutsForDate",
	async (params: RetryPayoutByDateParams, { rejectWithValue }) => {
		try {
			const response = await retryPayoutsForDateAsAdmin(params);
			return response;
		} catch (error: any) {
			// If error is an API response object with data, extract the message from data
			if (error && error.data && error.data.message) {
				return rejectWithValue({
					message: error.data.message,
					details: error.data.details || null,
					success: false,
				});
			}
			return rejectWithValue({
				message: error.message || "Failed to retry payouts for date",
				details: null,
				success: false,
			});
		}
	},
);

/**
 * Retry a specific payout by cycle ID and payout index
 */
export const retrySpecificPayout = createAsyncThunk(
	"adminPayout/retrySpecificPayout",
	async (params: RetrySpecificPayoutParams, { rejectWithValue }) => {
		try {
			const response = await retrySpecificPayoutAsAdmin(params);
			return response;
		} catch (error: any) {
			// If error is an API response object with data, extract the message from data
			if (error && error.data && error.data.message) {
				return rejectWithValue({
					message: error.data.message,
					details: error.data.details || null,
					success: false,
				});
			}
			return rejectWithValue({
				message: error.message || "Failed to retry specific payout",
				details: null,
				success: false,
			});
		}
	},
);

/**
 * Retry all missed/failed payouts
 */
export const retryAllPayouts = createAsyncThunk(
	"adminPayout/retryAllPayouts",
	async (_, { rejectWithValue }) => {
		try {
			const response = await retryAllPayoutsAsAdmin();
			return response;
		} catch (error: any) {
			return rejectWithValue(error.message || "Failed to retry all payouts");
		}
	},
);

// ==================== Filtered Payout Actions ====================

/**
 * Get payouts for a specific circle
 */
export const getPayoutsByCircleAsAdmin = createAsyncThunk(
	"adminPayout/getPayoutsByCircle",
	async (circleId: string, { rejectWithValue }) => {
		try {
			const response = await fetchPayoutsByCircleAsAdmin(circleId);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch payouts by circle",
			);
		}
	},
);

/**
 * Get payouts for a specific member
 */
export const getPayoutsByMemberAsAdmin = createAsyncThunk(
	"adminPayout/getPayoutsByMember",
	async (memberId: string, { rejectWithValue }) => {
		try {
			const response = await fetchPayoutsByMemberAsAdmin(memberId);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch payouts by member",
			);
		}
	},
);

/**
 * Get payouts for a specific cycle
 */
export const getPayoutsByCycleAsAdmin = createAsyncThunk(
	"adminPayout/getPayoutsByCycle",
	async (cycleId: string, { rejectWithValue }) => {
		try {
			const response = await fetchPayoutsByCycleAsAdmin(cycleId);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch payouts by cycle",
			);
		}
	},
);

/**
 * Get detailed payout info for a specific circle with date filtering
 * Used for the circle payout modal with calendar
 */
export const getCirclePayoutDetailsAsAdmin = createAsyncThunk(
	"adminPayout/getCirclePayoutDetails",
	async (params: CirclePayoutDetailsParams, { rejectWithValue }) => {
		try {
			const response = await fetchCirclePayoutDetailsAsAdmin(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch circle payout details",
			);
		}
	},
);
